import React, { useMemo, useState } from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import RequestTrainingButton from '@/components/competency/RequestTrainingButton';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Descriptions,
  Form,
  Input,
  Select,
  Steps,
  message,
  Tabs,
  Switch,
  Modal,
  Alert,
  Divider,
  DatePicker,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  FileOutlined,
  EditOutlined,
  LinkOutlined,
  PlayCircleOutlined,
  PrinterOutlined,
  CameraOutlined,
  PlusOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  useNCR,
  useUpdateNCR,
  useCloseNCR,
  useUpdateNCRStatus,
  useSubmitNCRForApproval,
  useSetDisposition,
  useRequireCAPA,
  useVerifyEffectiveness,
  useLinkCAPA,
  useNcrCapas,
  qualityKeys,
} from '../../hooks/useQuality';
import { useCAPAs } from '../../hooks/useQuality';
import DocumentList from '../../components/documents/DocumentList';
import NCRRootCausePanel from '../../components/quality/NCRRootCausePanel';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import DocumentUpload from '../../components/documents/DocumentUpload';
import { useUsers } from '@/hooks/useUsers';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { ENTITY_TYPE_NCR_RECORD } from '@/constants/approval-entity-types';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const NCRDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dispositionForm] = Form.useForm();
  const [effectivenessForm] = Form.useForm();
  const [closeForm] = Form.useForm();
  const [linkCAPAForm] = Form.useForm();
  const [isDispositionModalVisible, setIsDispositionModalVisible] = useState(false);
  const [isEffectivenessModalVisible, setIsEffectivenessModalVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [isLinkCAPAModalVisible, setIsLinkCAPAModalVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [activeTab, setActiveTab] = useState('details');

  const { data: ncr, isLoading, refetch } = useNCR(id || '');
  const { data: capas = [] } = useCAPAs();
  const { data: linkedCapas = [], refetch: refetchLinkedCapas } = useNcrCapas(id || '');
  const { data: users = [] } = useUsers({ is_active: true });
  const { data: departments = [] } = useDepartmentsForSelection();
  const updateMutation = useUpdateNCR();
  const closeMutation = useCloseNCR();
  const updateStatusMutation = useUpdateNCRStatus();
  const submitApprovalMutation = useSubmitNCRForApproval();
  const setDispositionMutation = useSetDisposition();
  const requireCAPAMutation = useRequireCAPA();
  const verifyEffectivenessMutation = useVerifyEffectiveness();
  const linkCAPAMutation = useLinkCAPA();
  const { resolve } = useUserDisplayResolver();

  const userOptions = useMemo(
    () =>
      (users as any[]).map((u) => ({
        value: u.id,
        label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.id,
      })),
    [users]
  );

  const departmentLabel = useMemo(() => {
    if (ncr?.department_name) return ncr.department_name;
    if (!ncr?.department_id) return null;
    const d = departments.find((x) => x.id === ncr.department_id);
    return d ? `${d.department_code} - ${d.department_name}` : ncr.department_id;
  }, [departments, ncr?.department_id, ncr?.department_name]);

  const watchedDisposition = Form.useWatch('disposition', dispositionForm);
  const isConcessionDisposition = watchedDisposition === 'Release Under Concession';
  const productDispositions = [
    'Use As Is',
    'Rework',
    'Repair',
    'Reject',
    'Scrap',
    'Regrade',
    'Release Under Concession',
  ];
  const needsCorrection =
    watchedDisposition &&
    productDispositions.includes(watchedDisposition) &&
    watchedDisposition !== 'Pending';

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      Open: 'red',
      'Under Investigation': 'orange',
      'Root Cause Analysis': 'processing',
      'CAPA Assigned': 'blue',
      'Corrective Action': 'cyan',
      'Pending Approval': 'gold',
      Verification: 'purple',
      Closed: 'success',
      Rejected: 'default',
    };
    return colors[status] || 'default';
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      Critical: 'red',
      Major: 'orange',
      Minor: 'yellow',
    };
    return colors[severity] || 'default';
  };

  const getCurrentStep = () => {
    const statusMap: Record<string, number> = {
      Open: 0,
      'Under Investigation': 1,
      'Root Cause Analysis': 2,
      'CAPA Assigned': 3,
      'Corrective Action': 4,
      'Pending Approval': 4,
      Verification: 5,
      Closed: 6,
    };
    return statusMap[ncr?.status || 'Open'] || 0;
  };

  const getNextStatusOptions = () => {
    if (ncr?.status === 'Pending Approval') return [];
    const statusTransitions: Record<string, string[]> = {
      Open: ['Under Investigation', 'Rejected'],
      'Under Investigation': ['Root Cause Analysis', 'Open'],
      'Root Cause Analysis': ['CAPA Assigned', 'Corrective Action', 'Under Investigation'],
      'CAPA Assigned': ['Corrective Action', 'Root Cause Analysis'],
      'Corrective Action': ['Verification', 'CAPA Assigned'],
      Verification: ['Closed', 'Corrective Action'],
      Closed: ['Corrective Action', 'Verification'],
      Rejected: [],
    };
    return statusTransitions[ncr?.status || 'Open'] || [];
  };

  const handleStatusUpdate = async (newStatus: string) => {
    try {
      await updateStatusMutation.mutateAsync({ id: id!, status: newStatus });
      setIsStatusModalVisible(false);
      refetch();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDispositionSubmit = async (values: any) => {
    try {
      const toDate = (v: any) => (v ? v.format('YYYY-MM-DD') : null);
      await setDispositionMutation.mutateAsync({
        id: id!,
        data: {
          disposition: values.disposition,
          disposition_notes: values.disposition_notes,
          disposition_done_by: values.disposition_done_by || null,
          correction_target_date: toDate(values.correction_target_date),
          correction_action: values.correction_action,
          correction_completed_by: values.correction_completed_by || null,
          correction_completed_date: toDate(values.correction_completed_date),
          correction_delay_justification: values.correction_delay_justification,
          concession_rationale: values.concession_rationale,
          concession_party_notified: values.concession_party_notified ?? null,
          concession_evidence: values.concession_evidence,
          concession_approved_by_name: values.concession_approved_by_name,
          concession_approved_at: toDate(values.concession_approved_at),
        },
      });
      setIsDispositionModalVisible(false);
      dispositionForm.resetFields();
      refetch();
    } catch (error) {
      console.error('Error setting disposition:', error);
    }
  };

  const handleEffectivenessSubmit = async (values: any) => {
    try {
      await verifyEffectivenessMutation.mutateAsync({
        id: id!,
        data: {
          effectiveness_verified: values.effectiveness_verified,
          effectiveness_notes: values.effectiveness_notes,
          verification_method: values.verification_method,
          verified_by_designation: values.verified_by_designation,
          verification_evidence_notes: values.verification_evidence_notes,
        },
      });
      setIsEffectivenessModalVisible(false);
      effectivenessForm.resetFields();
      refetch();
    } catch (error) {
      console.error('Error verifying effectiveness:', error);
    }
  };

  const handleClose = async (values: any) => {
    try {
      await closeMutation.mutateAsync({ id: id!, notes: values.notes });
      setIsClosing(false);
      closeForm.resetFields();
      refetch();
      message.success('NCR closed successfully');
    } catch (error) {
      console.error('Error closing NCR:', error);
    }
  };

  const handleRequireCAPA = async (capaRequired: boolean) => {
    try {
      await requireCAPAMutation.mutateAsync({ id: id!, capaRequired });
      refetch();
    } catch (error) {
      console.error('Error updating CAPA requirement:', error);
    }
  };

  const handleLinkCAPA = async (capaId: string) => {
    try {
      await linkCAPAMutation.mutateAsync({ id: id!, capaId });
      setIsLinkCAPAModalVisible(false);
      linkCAPAForm.resetFields();
      await Promise.all([refetch(), refetchLinkedCapas()]);
    } catch (error) {
      console.error('Error linking CAPA:', error);
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!ncr) {
    return <div>NCR not found</div>;
  }

  const steps = [
    { title: 'Open', description: 'NCR identified' },
    { title: 'Investigation', description: 'Under investigation' },
    { title: 'Root Cause', description: 'Root cause analysis' },
    { title: 'CAPA', description: 'CAPA assigned' },
    { title: 'Action', description: 'Corrective action' },
    { title: 'Verification', description: 'Effectiveness verification' },
    { title: 'Closed', description: 'NCR closed' },
  ];

  const nextStatusOptions = getNextStatusOptions();
  const canSubmitApproval =
    Boolean(id) &&
    ncr.status !== 'Closed' &&
    ncr.status !== 'Rejected' &&
    ncr.status !== 'Pending Approval' &&
    !ncr.approval_instance_id &&
    Boolean(ncr.disposition) &&
    ncr.disposition !== 'Pending';
  const showApproval =
    ncr.status === 'Pending Approval' || Boolean(ncr.approval_instance_id);

  return (
    <div style={{ padding: '24px' }}>
      {id ? <AiPageContextSync module="quality" docType="NCR" docId={id} /> : null}
      <Space style={{ marginBottom: 24 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/qms/ncrs')}>
          Back
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          NCR: {ncr.ncr_number}
        </Title>
        <Button icon={<EditOutlined />} onClick={() => navigate(`/qms/ncrs/${id}/edit`)}>
          Edit
        </Button>
        <Button
          icon={<PrinterOutlined />}
          onClick={() => window.open(`/print/qms/ncrs/${id}`, '_blank', 'noopener,noreferrer')}
        >
          Print
        </Button>
        <RequestTrainingButton
          sourceType="NCR"
          sourceId={ncr.id}
          defaultNotes={`${ncr.ncr_number}${ncr.root_cause_analysis ? `: ${ncr.root_cause_analysis}` : ''}`}
        />
        {nextStatusOptions.length > 0 && ncr.status !== 'Pending Approval' && (
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={() => setIsStatusModalVisible(true)}
          >
            {ncr.status === 'Closed' ? 'Reopen / Update Status' : 'Update Status'}
          </Button>
        )}
        {canSubmitApproval && (
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={submitApprovalMutation.isPending}
            onClick={() => submitApprovalMutation.mutate(id!)}
          >
            Submit for approval
          </Button>
        )}
      </Space>

      {ncr.status === 'Pending Approval' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Pending approval"
          description="Disposition is awaiting approval. Use the Approval panel or Approval Inbox to approve or reject."
        />
      )}

      {showApproval && id && (
        <Card title="Approval" style={{ marginBottom: 16 }}>
          <ApprovalPanel
            entityType={ENTITY_TYPE_NCR_RECORD}
            entityId={id}
            extraInvalidateQueryKeys={[qualityKeys.ncr(id), qualityKeys.ncrs()]}
          />
        </Card>
      )}

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12} xl={8}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">Status</Text>
                <br />
                <Tag color={getStatusColor(ncr.status)} style={{ marginTop: 4, fontSize: '14px', padding: '4px 12px' }}>
                  {ncr.status}
                </Tag>
              </div>
              <div>
                <Text type="secondary">Severity</Text>
                <br />
                <Tag color={getSeverityColor(ncr.severity)} style={{ marginTop: 4, fontSize: '14px', padding: '4px 12px' }}>
                  {ncr.severity}
                </Tag>
              </div>
              {ncr.category && (
                <div>
                  <Text type="secondary">Category</Text>
                  <br />
                  <Text strong>{ncr.category}</Text>
                </div>
              )}
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={8}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text type="secondary">Source</Text>
                <br />
                <Text strong>{ncr.source}</Text>
              </div>
              <div>
                <Text type="secondary">Identified Date</Text>
                <br />
                <Text>{dayjs(ncr.identified_date).format('DD/MM/YYYY')}</Text>
              </div>
              {ncr.source_reference && (
                <div>
                  <Text type="secondary">Source Reference</Text>
                  <br />
                  <Text>{ncr.source_reference}</Text>
                </div>
              )}
            </Space>
          </Card>
        </Col>
        <Col xs={24} md={12} xl={8}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              {(ncr.capa_id || linkedCapas.length > 0) && (
                <div>
                  <Text type="secondary">Linked CAPA{linkedCapas.length > 1 ? 's' : ''}</Text>
                  <br />
                  {ncr.capa_id ? (
                    <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/qms/capas/${ncr.capa_id}`)}>
                      Primary CAPA
                    </Button>
                  ) : null}
                  {linkedCapas.length > 0 && (
                    <Text type="secondary" style={{ display: 'block' }}>
                      {linkedCapas.length} linked — see CAPA tab
                    </Text>
                  )}
                </div>
              )}
              {ncr.closed_date && (
                <div>
                  <Text type="secondary">Closed Date</Text>
                  <br />
                  <Text>{dayjs(ncr.closed_date).format('DD/MM/YYYY')}</Text>
                </div>
              )}
              <div>
                <Text type="secondary">CAPA Required</Text>
                <br />
                <Switch
                  checked={ncr.capa_required}
                  onChange={handleRequireCAPA}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                />
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 24 }}>
        <Steps current={getCurrentStep()} items={steps} />
      </Card>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'details',
              label: 'Details',
              children: (
                <>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    <Button icon={<EditOutlined />} onClick={() => navigate(`/qms/ncrs/${id}/edit`)}>
                      Edit fields
                    </Button>
                  </div>
                  <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
                    <Descriptions.Item label="NCR Number" span={2}>
                      <Text strong>{ncr.ncr_number}</Text>
                    </Descriptions.Item>
                    {ncr.project && (
                      <Descriptions.Item label="Project" span={2}>
                        {[ncr.project.project_number, ncr.project.project_name].filter(Boolean).join(' — ')}
                      </Descriptions.Item>
                    )}
                    {ncr.work_order && (
                      <Descriptions.Item label="Work Order" span={2}>
                        {ncr.work_order.work_order_number || ncr.work_order_id}
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Department">
                      {departmentLabel || ncr.department_name || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Potential NC">
                      {ncr.is_potential_nc ? 'Yes' : 'No'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Correction owner">
                      {ncr.correction_owner_user_id
                        ? resolve(ncr.correction_owner_user_id)
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="CA owner">
                      {ncr.capa_owner_user_id ? resolve(ncr.capa_owner_user_id) : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="MOC Required">
                      {ncr.moc_required ? 'Yes' : 'No'}
                    </Descriptions.Item>
                    <Descriptions.Item label="MOC Ref">
                      {ncr.moc_id ? (
                        <Link to={`/grc/moc/${ncr.moc_id}`}>
                          {ncr.moc_ref_no || ncr.moc_id}
                        </Link>
                      ) : (
                        ncr.moc_ref_no || '—'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Risk Reference" span={2}>
                      {ncr.risk_assessment_id ? (
                        <Link to={`/grc/risk-assessments/${ncr.risk_assessment_id}`}>
                          {ncr.risk_reference || ncr.risk_assessment_id}
                        </Link>
                      ) : (
                        ncr.risk_reference || '—'
                      )}
                    </Descriptions.Item>
                    {ncr.source_reference && (
                      <Descriptions.Item label="Customer / External NCR No." span={2}>
                        {ncr.source_reference}
                      </Descriptions.Item>
                    )}
                    {ncr.supplier_name && (
                      <Descriptions.Item label="Supplier name">{ncr.supplier_name}</Descriptions.Item>
                    )}
                    {ncr.supplier_contact_person && (
                      <Descriptions.Item label="Supplier contact">
                        {ncr.supplier_contact_person}
                      </Descriptions.Item>
                    )}
                    {ncr.package_no && (
                      <Descriptions.Item label="Package No">{ncr.package_no}</Descriptions.Item>
                    )}
                    {ncr.po_number && (
                      <Descriptions.Item label="PO No">{ncr.po_number}</Descriptions.Item>
                    )}
                    {ncr.po_line && (
                      <Descriptions.Item label="PO Line">{ncr.po_line}</Descriptions.Item>
                    )}
                    {ncr.part_item_no && (
                      <Descriptions.Item label="Part / Item No.">{ncr.part_item_no}</Descriptions.Item>
                    )}
                    {ncr.part_description && (
                      <Descriptions.Item label="Part description">{ncr.part_description}</Descriptions.Item>
                    )}
                    {ncr.equipment_description && (
                      <Descriptions.Item label="Equipment description">
                        {ncr.equipment_description}
                      </Descriptions.Item>
                    )}
                    {ncr.tag_number && (
                      <Descriptions.Item label="Tag number">{ncr.tag_number}</Descriptions.Item>
                    )}
                    {ncr.drawing_no && (
                      <Descriptions.Item label="Drawing Nr">{ncr.drawing_no}</Descriptions.Item>
                    )}
                    {ncr.drawing_revision && (
                      <Descriptions.Item label="Drawing Revision">{ncr.drawing_revision}</Descriptions.Item>
                    )}
                    {ncr.specification_number && (
                      <Descriptions.Item label="Specification">{ncr.specification_number}</Descriptions.Item>
                    )}
                    {ncr.reference_documents && (
                      <Descriptions.Item label="Reference documents" span={2}>
                        {ncr.reference_documents}
                      </Descriptions.Item>
                    )}
                    {ncr.international_standard && (
                      <Descriptions.Item label="International standard" span={2}>
                        {ncr.international_standard}
                      </Descriptions.Item>
                    )}
                    {ncr.actual_requirements && (
                      <Descriptions.Item label="Actual Requirements" span={2}>
                        <Text>{ncr.actual_requirements}</Text>
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Description of Non-Conformance" span={2}>
                      <Text>{ncr.description}</Text>
                    </Descriptions.Item>
                    {ncr.additional_information && (
                      <Descriptions.Item label="Additional Information" span={2}>
                        <Text>{ncr.additional_information}</Text>
                      </Descriptions.Item>
                    )}
                    {ncr.proposed_corrective_action && (
                      <Descriptions.Item label="Proposed corrective action(s)" span={2}>
                        <Text>{ncr.proposed_corrective_action}</Text>
                      </Descriptions.Item>
                    )}
                    {ncr.customer_comments && (
                      <Descriptions.Item label="Customer comments / recommendations" span={2}>
                        <Text>{ncr.customer_comments}</Text>
                      </Descriptions.Item>
                    )}
                    {ncr.customer_decision && (
                      <Descriptions.Item label="Customer decision">
                        <Tag color={ncr.customer_decision === 'Accepted' ? 'success' : 'error'}>
                          {ncr.customer_decision}
                        </Tag>
                      </Descriptions.Item>
                    )}
                    {ncr.customer_decision_date && (
                      <Descriptions.Item label="Decision date">
                        {dayjs(ncr.customer_decision_date).format('DD/MM/YYYY')}
                      </Descriptions.Item>
                    )}
                    {(ncr.supplier_signer_name || ncr.supplier_signer_title) && (
                      <Descriptions.Item label="Supplier signature">
                        {[ncr.supplier_signer_name, ncr.supplier_signer_title].filter(Boolean).join(' — ')}
                      </Descriptions.Item>
                    )}
                    {(ncr.customer_signer_name || ncr.customer_signer_title) && (
                      <Descriptions.Item label="Customer signature">
                        {[ncr.customer_signer_name, ncr.customer_signer_title].filter(Boolean).join(' — ')}
                      </Descriptions.Item>
                    )}
                    {ncr.location && (
                      <Descriptions.Item label="Location">{ncr.location}</Descriptions.Item>
                    )}
                    {ncr.affected_items && (
                      <Descriptions.Item label="Affected Items">{ncr.affected_items}</Descriptions.Item>
                    )}
                    {ncr.containment_action && (
                      <Descriptions.Item label="Containment / immediate action" span={2}>
                        <Text>{ncr.containment_action}</Text>
                      </Descriptions.Item>
                    )}
                    {ncr.quantity_affected != null && (
                      <Descriptions.Item label="Quantity Affected">
                        {ncr.quantity_affected}
                        {ncr.quantity_uom ? ` ${ncr.quantity_uom}` : ''}
                      </Descriptions.Item>
                    )}
                    {ncr.opportunity_for_improvement && (
                      <Descriptions.Item label="Opportunity for improvement">Yes</Descriptions.Item>
                    )}
                    <Descriptions.Item label="Disposition">
                      <Tag>{ncr.disposition || 'Pending'}</Tag>
                    </Descriptions.Item>
                    {ncr.disposition_approved_by && (
                      <Descriptions.Item label="Disposition Approved By">
                        {resolve(ncr.disposition_approved_by)}
                      </Descriptions.Item>
                    )}
                    {ncr.disposition_done_by && (
                      <Descriptions.Item label="Disposition done by">
                        {resolve(ncr.disposition_done_by)}
                      </Descriptions.Item>
                    )}
                    {ncr.disposition_date && (
                      <Descriptions.Item label="Disposition Date">
                        {dayjs(ncr.disposition_date).format('DD/MM/YYYY')}
                      </Descriptions.Item>
                    )}
                    {ncr.disposition_notes && (
                      <Descriptions.Item label="Disposition Notes" span={2}>
                        <Text>{ncr.disposition_notes}</Text>
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  <Divider orientation="left" plain>
                    Correction (this instance)
                  </Divider>
                  <Descriptions bordered column={2} style={{ marginBottom: 24 }} size="small">
                    <Descriptions.Item label="Correction action" span={2}>
                      {ncr.correction_action || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Target date">
                      {ncr.correction_target_date
                        ? dayjs(ncr.correction_target_date).format('DD/MM/YYYY')
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Completed by">
                      {ncr.correction_completed_by
                        ? resolve(ncr.correction_completed_by)
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Completed date">
                      {ncr.correction_completed_date
                        ? dayjs(ncr.correction_completed_date).format('DD/MM/YYYY')
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Late justification" span={2}>
                      {ncr.correction_delay_justification || '—'}
                    </Descriptions.Item>
                  </Descriptions>

                  {ncr.disposition === 'Release Under Concession' && (
                    <>
                      <Divider orientation="left" plain>
                        Concession
                      </Divider>
                      <Descriptions bordered column={2} style={{ marginBottom: 24 }} size="small">
                        <Descriptions.Item label="Rationale" span={2}>
                          {ncr.concession_rationale || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Party / customer notified">
                          {ncr.concession_party_notified == null
                            ? '—'
                            : ncr.concession_party_notified
                              ? 'Yes'
                              : 'No'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Approver">
                          {ncr.concession_approved_by_name || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Approval date">
                          {ncr.concession_approved_at
                            ? dayjs(ncr.concession_approved_at).format('DD/MM/YYYY')
                            : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Evidence" span={2}>
                          {ncr.concession_evidence || '—'}
                        </Descriptions.Item>
                      </Descriptions>
                    </>
                  )}

                  <Divider orientation="left" plain>
                    Corrective Action / CAPA summary
                  </Divider>
                  <Descriptions bordered column={2} style={{ marginBottom: 24 }} size="small">
                    <Descriptions.Item label="Proposed / CA narrative" span={2}>
                      {ncr.proposed_corrective_action || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="NCR CA target">
                      {ncr.capa_target_date
                        ? dayjs(ncr.capa_target_date).format('DD/MM/YYYY')
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="CAPA target (linked)">
                      {ncr.capa?.target_completion_date
                        ? dayjs(ncr.capa.target_completion_date).format('DD/MM/YYYY')
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="CA late justification" span={2}>
                      {ncr.capa_delay_justification ||
                        ncr.capa?.extension_reason ||
                        '—'}
                    </Descriptions.Item>
                  </Descriptions>

                  <Divider orientation="left" plain>
                    Audit / closure
                  </Divider>
                  <Descriptions bordered column={2} style={{ marginBottom: 24 }} size="small">
                    <Descriptions.Item label="Identified by">
                      {ncr.identified_by ? resolve(ncr.identified_by) : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Identified date">
                      {ncr.identified_date
                        ? dayjs(ncr.identified_date).format('DD/MM/YYYY')
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Closed by">
                      {ncr.closed_by ? resolve(ncr.closed_by) : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Closed date">
                      {ncr.closed_date
                        ? dayjs(ncr.closed_date).format('DD/MM/YYYY')
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Closure notes" span={2}>
                      {ncr.closure_notes || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Effectiveness verified by">
                      {ncr.effectiveness_verified_by
                        ? resolve(ncr.effectiveness_verified_by)
                        : '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Verification method">
                      {ncr.verification_method || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Verified by designation">
                      {ncr.verified_by_designation || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Verification evidence" span={2}>
                      {ncr.verification_evidence_notes || '—'}
                    </Descriptions.Item>
                  </Descriptions>

                  {ncr.root_causes?.length || ncr.root_cause_analysis ? (
                    <div style={{ marginTop: 24 }}>
                      <NCRRootCausePanel ncr={ncr} ncrId={id!} onChanged={() => refetch()} />
                    </div>
                  ) : null}

                  {ncr.effectiveness_verified && ncr.effectiveness_notes && (
                    <div style={{ marginTop: 24 }}>
                      <Text strong>Effectiveness Verification:</Text>
                      <Card style={{ marginTop: 8 }}>
                        <Text>{ncr.effectiveness_notes}</Text>
                        {ncr.effectiveness_verified_date && (
                          <div style={{ marginTop: 8 }}>
                            <Tag color="success">Verified</Tag>
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                              on {dayjs(ncr.effectiveness_verified_date).format('DD/MM/YYYY')}
                            </Text>
                          </div>
                        )}
                      </Card>
                    </div>
                  )}

                  <Divider />

                  <Space direction="vertical" style={{ width: '100%' }}>
                    {!ncr.root_causes?.length &&
                      !ncr.root_cause_analysis &&
                      (ncr.status === 'Under Investigation' || ncr.status === 'Root Cause Analysis') && (
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => setActiveTab('root-cause')}
                      >
                        Go to Root Cause Analysis
                      </Button>
                    )}

                    {ncr.status !== 'Closed' && ncr.status !== 'Rejected' && !ncr.disposition && (
                      <Button
                        icon={<CheckCircleOutlined />}
                        onClick={() => setIsDispositionModalVisible(true)}
                      >
                        Set Disposition
                      </Button>
                    )}

                    {ncr.status === 'Verification' && !ncr.effectiveness_verified && (
                      <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => setIsEffectivenessModalVisible(true)}
                      >
                        Verify Effectiveness
                      </Button>
                    )}

                    {!ncr.capa_id && ncr.capa_required && (
                      <Button
                        icon={<LinkOutlined />}
                        onClick={() => setIsLinkCAPAModalVisible(true)}
                      >
                        Link CAPA
                      </Button>
                    )}

                    {ncr.status !== 'Closed' && ncr.status !== 'Rejected' && (
                      <Button
                        danger
                        icon={<CheckCircleOutlined />}
                        onClick={() => setIsClosing(true)}
                      >
                        Close NCR
                      </Button>
                    )}
                  </Space>
                </>
              ),
            },
            {
              key: 'root-cause',
              label: 'Root Cause Analysis',
              children: <NCRRootCausePanel ncr={ncr} ncrId={id!} onChanged={() => refetch()} />,
            },
            {
              key: 'disposition',
              label: 'Disposition',
              children: (
                <div>
                  {ncr.disposition ? (
                    <Card>
                      <Descriptions bordered column={1}>
                        <Descriptions.Item label="Disposition">
                          <Tag>{ncr.disposition}</Tag>
                        </Descriptions.Item>
                        {ncr.disposition_approved_by && (
                          <Descriptions.Item label="Approved By">
                            {resolve(ncr.disposition_approved_by)}
                          </Descriptions.Item>
                        )}
                        {ncr.disposition_date && (
                          <Descriptions.Item label="Approval Date">
                            {dayjs(ncr.disposition_date).format('DD/MM/YYYY')}
                          </Descriptions.Item>
                        )}
                        {ncr.disposition_notes && (
                          <Descriptions.Item label="Notes">
                            <Text>{ncr.disposition_notes}</Text>
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                      {ncr.status !== 'Closed' && (
                        <div style={{ marginTop: 16 }}>
                          <Button
                            icon={<EditOutlined />}
                            onClick={() => {
                              dispositionForm.setFieldsValue({
                                disposition: ncr.disposition,
                                disposition_notes: ncr.disposition_notes,
                                disposition_done_by: ncr.disposition_done_by || undefined,
                                correction_target_date: ncr.correction_target_date
                                  ? dayjs(ncr.correction_target_date)
                                  : undefined,
                                correction_action: ncr.correction_action,
                                correction_completed_by: ncr.correction_completed_by || undefined,
                                correction_completed_date: ncr.correction_completed_date
                                  ? dayjs(ncr.correction_completed_date)
                                  : undefined,
                                correction_delay_justification: ncr.correction_delay_justification,
                                concession_rationale: ncr.concession_rationale,
                                concession_party_notified: ncr.concession_party_notified,
                                concession_evidence: ncr.concession_evidence,
                                concession_approved_by_name: ncr.concession_approved_by_name,
                                concession_approved_at: ncr.concession_approved_at
                                  ? dayjs(ncr.concession_approved_at)
                                  : undefined,
                              });
                              setIsDispositionModalVisible(true);
                            }}
                          >
                            Update Disposition
                          </Button>
                        </div>
                      )}
                    </Card>
                  ) : (
                    <Alert
                      message="No Disposition Set"
                      description="Set the disposition for this NCR to determine how to handle the non-conformance."
                      type="warning"
                      showIcon
                      action={
                        <Button
                          type="primary"
                          onClick={() => setIsDispositionModalVisible(true)}
                        >
                          Set Disposition
                        </Button>
                      }
                    />
                  )}
                </div>
              ),
            },
            {
              key: 'capa',
              label: 'CAPA',
              children: (
                <div>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Correction vs CAPA"
                    description="Correction (Disposition tab) fixes this NC instance. CAPA below is systemic corrective / preventive action. Primary CAPA drives NCR status; additional CAPAs can be linked."
                  />
                  <Space style={{ marginBottom: 16 }} wrap>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() =>
                        navigate(`/qms/capas/new?ncr_id=${id}`, {
                          state: {
                            ncr_id: id,
                            source: 'NCR',
                            source_reference: ncr.ncr_number,
                            description: `CAPA from NCR ${ncr.ncr_number}: ${ncr.description?.slice(0, 200) || ''}`,
                            problem_statement: [
                              ncr.description,
                              ncr.root_cause_analysis
                                ? `Root cause: ${ncr.root_cause_analysis}`
                                : null,
                              ncr.containment_action
                                ? `Containment: ${ncr.containment_action}`
                                : null,
                            ]
                              .filter(Boolean)
                              .join('\n\n'),
                            project_id: ncr.project_id,
                            department_id: ncr.department_id,
                            action_plan: ncr.root_cause_analysis
                              ? `Address root cause: ${ncr.root_cause_analysis}`
                              : undefined,
                          },
                        })
                      }
                    >
                      Create CAPA from this NCR
                    </Button>
                    <Button icon={<LinkOutlined />} onClick={() => setIsLinkCAPAModalVisible(true)}>
                      Link existing CAPA
                    </Button>
                  </Space>

                  {linkedCapas.length > 0 ? (
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                      {linkedCapas.map((capa) => (
                        <Card
                          key={capa.id}
                          size="small"
                          title={
                            <Space>
                              <Text strong>{capa.capa_number}</Text>
                              <Tag>{capa.status}</Tag>
                              {capa.id === ncr.capa_id && <Tag color="blue">Primary</Tag>}
                            </Space>
                          }
                          extra={
                            <Button type="link" onClick={() => navigate(`/qms/capas/${capa.id}`)}>
                              Open
                            </Button>
                          }
                        >
                          <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label="Type">{capa.type}</Descriptions.Item>
                            <Descriptions.Item label="Progress">
                              {capa.progress_percentage ?? 0}%
                            </Descriptions.Item>
                            <Descriptions.Item label="Target">
                              {capa.target_completion_date
                                ? dayjs(capa.target_completion_date).format('DD/MM/YYYY')
                                : '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Actual completion">
                              {capa.actual_completion_date
                                ? dayjs(capa.actual_completion_date).format('DD/MM/YYYY')
                                : '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Description" span={2}>
                              {capa.description}
                            </Descriptions.Item>
                          </Descriptions>
                        </Card>
                      ))}
                    </Space>
                  ) : ncr.capa_required ? (
                    <Alert
                      message="CAPA Required"
                      description="Create a CAPA from this NCR or link an existing one to proceed with corrective action."
                      type="warning"
                      showIcon
                    />
                  ) : (
                    <Alert
                      message="No CAPAs linked"
                      description="This NCR has no linked CAPAs. Enable CAPA required above if systemic action is needed."
                      type="info"
                      showIcon
                    />
                  )}
                </div>
              ),
            },
            {
              key: 'documents',
              label: (
                <span>
                  <FileOutlined />
                  Documents
                </span>
              ),
              children: (
                <div>
                  <Alert
                    type="info"
                    showIcon
                    icon={<CameraOutlined />}
                    style={{ marginBottom: 16 }}
                    message="Photo / evidence of non-conformance"
                    description={
                      <Paragraph style={{ marginBottom: 0 }}>
                        Upload photos or supporting evidence of the NC here. Prefer clear images of
                        the defect, location, and identification marks. These appear as attachments
                        on the NTES-QSF-QA-QC-09 printout.
                      </Paragraph>
                    }
                  />
                  <div style={{ marginBottom: 16 }}>
                    <DocumentUpload
                      entityType="NCRRecord"
                      entityId={id || ''}
                      buttonText="Upload NC photo / evidence"
                      defaultValues={{
                        document_type: 'Image',
                        document_category: 'NC Evidence',
                        tags: 'ncr,photo,evidence',
                        description: 'Photo / evidence of non-conformance',
                      }}
                      onSuccess={() => {
                        // Refetch will be handled by DocumentList component
                      }}
                    />
                  </div>
                  <DocumentList
                    entityType="NCRRecord"
                    entityId={id || ''}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Disposition Modal */}
      <Modal
        title="Set Disposition / Correction"
        open={isDispositionModalVisible}
        onCancel={() => {
          setIsDispositionModalVisible(false);
          dispositionForm.resetFields();
        }}
        onOk={() => dispositionForm.submit()}
        confirmLoading={setDispositionMutation.isPending}
        width={720}
      >
        <Form form={dispositionForm} layout="vertical" onFinish={handleDispositionSubmit}>
          <Form.Item
            name="disposition"
            label="Disposition"
            rules={[{ required: true, message: 'Please select disposition' }]}
          >
            <Select placeholder="Select disposition">
              <Select.Option value="Use As Is">Use As Is</Select.Option>
              <Select.Option value="Rework">Rework</Select.Option>
              <Select.Option value="Repair">Repair</Select.Option>
              <Select.Option value="Regrade">Regrade</Select.Option>
              <Select.Option value="Release Under Concession">Release Under Concession</Select.Option>
              <Select.Option value="Reject">Reject</Select.Option>
              <Select.Option value="Scrap">Scrap</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="disposition_notes" label="Disposition Notes">
            <TextArea rows={2} placeholder="Enter disposition notes and justification" />
          </Form.Item>
          <Form.Item name="disposition_done_by" label="Disposition done by">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={userOptions}
              placeholder="Who performed the disposition / correction"
            />
          </Form.Item>
          <Divider orientation="left" plain>
            Correction (this instance)
          </Divider>
          <Form.Item
            name="correction_action"
            label="Correction description"
            rules={
              needsCorrection
                ? [{ required: true, message: 'Correction is required for product dispositions' }]
                : undefined
            }
          >
            <TextArea rows={2} placeholder="Action taken to fix this instance" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="correction_target_date" label="Correction target date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="correction_completed_date" label="Completion date">
                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="correction_completed_by" label="Correction completed by">
            <Select allowClear showSearch optionFilterProp="label" options={userOptions} />
          </Form.Item>
          <Form.Item name="correction_delay_justification" label="Late justification">
            <TextArea rows={2} />
          </Form.Item>
          {isConcessionDisposition && (
            <>
              <Divider orientation="left" plain>
                Concession
              </Divider>
              <Form.Item
                name="concession_rationale"
                label="Rationale for release"
                rules={[{ required: true, message: 'Concession rationale is required' }]}
              >
                <TextArea rows={2} />
              </Form.Item>
              <Form.Item
                name="concession_party_notified"
                label="Relevant party / customer notified?"
                valuePropName="checked"
              >
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
              <Form.Item name="concession_evidence" label="Supporting evidence">
                <TextArea rows={2} />
              </Form.Item>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="concession_approved_by_name" label="Concession approver">
                    <Input placeholder="Name" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="concession_approved_at" label="Approval date">
                    <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}
        </Form>
      </Modal>

      {/* Effectiveness Verification Modal */}
      <Modal
        title="Verify Effectiveness"
        open={isEffectivenessModalVisible}
        onCancel={() => {
          setIsEffectivenessModalVisible(false);
          effectivenessForm.resetFields();
        }}
        onOk={() => effectivenessForm.submit()}
        confirmLoading={verifyEffectivenessMutation.isPending}
        width={640}
      >
        <Form form={effectivenessForm} layout="vertical" onFinish={handleEffectivenessSubmit}>
          <Form.Item
            name="effectiveness_verified"
            label="Effectiveness"
            rules={[{ required: true, message: 'Please verify effectiveness' }]}
          >
            <Select placeholder="Select effectiveness status">
              <Select.Option value={true}>Effective</Select.Option>
              <Select.Option value={false}>Not Effective</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="verification_method" label="Verification method">
            <Input placeholder="e.g. Re-inspection, document review" />
          </Form.Item>
          <Form.Item name="verified_by_designation" label="Verified by designation">
            <Input placeholder="Role / title" />
          </Form.Item>
          <Form.Item name="verification_evidence_notes" label="Evidence (photos / activities)">
            <TextArea rows={2} placeholder="Note evidence or refer to Documents tab" />
          </Form.Item>
          <Form.Item name="effectiveness_notes" label="Verification Notes">
            <TextArea rows={3} placeholder="Enter effectiveness verification notes" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Status Update Modal */}
      <Modal
        title="Update NCR Status"
        open={isStatusModalVisible}
        onCancel={() => {
          setIsStatusModalVisible(false);
          setSelectedStatus('');
        }}
        onOk={() => {
          if (selectedStatus) {
            handleStatusUpdate(selectedStatus);
          }
        }}
        confirmLoading={updateStatusMutation.isPending}
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Select new status"
          value={selectedStatus}
          onChange={setSelectedStatus}
        >
          {nextStatusOptions.map((status) => (
            <Select.Option key={status} value={status}>
              {status}
            </Select.Option>
          ))}
        </Select>
      </Modal>

      {/* Link CAPA Modal */}
      <Modal
        title="Link CAPA to NCR"
        open={isLinkCAPAModalVisible}
        onCancel={() => {
          setIsLinkCAPAModalVisible(false);
          linkCAPAForm.resetFields();
        }}
        onOk={() => linkCAPAForm.submit()}
      >
        <Form
          form={linkCAPAForm}
          layout="vertical"
          onFinish={(values) => {
            handleLinkCAPA(values.capa_id);
          }}
        >
          <Form.Item
            name="capa_id"
            label="Select CAPA"
            rules={[{ required: true, message: 'Please select a CAPA' }]}
          >
            <Select placeholder="Select CAPA" showSearch>
              {capas
                .filter((capa) => capa.status !== 'Closed' && capa.status !== 'Cancelled')
                .map((capa) => (
                  <Select.Option key={capa.id} value={capa.id}>
                    {capa.capa_number} - {capa.description?.substring(0, 50)}...
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Close NCR Modal */}
      <Modal
        title="Close NCR"
        open={isClosing}
        onCancel={() => {
          setIsClosing(false);
          closeForm.resetFields();
        }}
        onOk={() => closeForm.submit()}
        confirmLoading={closeMutation.isPending}
      >
        <Form form={closeForm} layout="vertical" onFinish={handleClose}>
          <Form.Item name="notes" label="Closure Notes">
            <TextArea rows={4} placeholder="Enter closure notes" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NCRDetailPage;
